import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { NodeEntry, NodeChildAssociationEntry } from '@alfresco/js-api';
import { SetSelectedNodesAction } from '@alfresco/aca-shared/store';

import { AppStore } from '../store/states/app.state';
import {
  SnackbarInfoAction,
  SnackbarErrorAction,
  SnackbarWarningAction,
  ReloadDocumentListAction,
  UndoDeleteNodesAction,
  SetSitesAction,
  SetCurrentSiteAction,
} from '../store/actions/app.custom-actions';
import { CustomContentManagementService } from './custom-content-management.service';
import { WkContentManagementService } from './wk-content-management.service';
import { NodeInfoService } from './node.service';

/**
 * Facade service that abstracts store interactions and node operations.
 * This breaks the circular dependency between effects and services by providing
 * a single point of access for action dispatching and service method calls.
 *
 * Services are lazily retrieved using Injector to avoid circular dependency issues.
 */
@Injectable({
  providedIn: 'root',
})
export class NodeActionsFacade {
  private customContentService: CustomContentManagementService;
  private wkContentManagementService: WkContentManagementService;
  private nodeInfoService: NodeInfoService;

  constructor(
    private readonly store: Store<AppStore>,
    private readonly injector: Injector
  ) {}

  // ============ Lazy Service Getters ============

  private getCustomContentService(): CustomContentManagementService {
    if (!this.customContentService) {
      this.customContentService =
        this.injector.get<CustomContentManagementService>(
          CustomContentManagementService
        );
    }
    return this.customContentService;
  }

  private getWkContentManagementService(): WkContentManagementService {
    if (!this.wkContentManagementService) {
      this.wkContentManagementService =
        this.injector.get<WkContentManagementService>(
          WkContentManagementService
        );
    }
    return this.wkContentManagementService;
  }

  private getNodeInfoService(): NodeInfoService {
    if (!this.nodeInfoService) {
      this.nodeInfoService =
        this.injector.get<NodeInfoService>(NodeInfoService);
    }
    return this.nodeInfoService;
  }

  // ============ Store Dispatch Methods ============

  /**
   * Generic dispatch method for any action
   */
  public dispatchAction(action: any): void {
    this.store.dispatch(action);
  }

  /**
   * Dispatch a snackbar info action
   */
  public dispatchSnackbarInfo(message: string, metadata?: any): void {
    this.store.dispatch(new SnackbarInfoAction(message, metadata));
  }

  /**
   * Dispatch a snackbar error action
   */
  public dispatchSnackbarError(message: string): void {
    this.store.dispatch(new SnackbarErrorAction(message));
  }

  /**
   * Dispatch a snackbar warning action
   */
  public dispatchSnackbarWarning(message: string): void {
    this.store.dispatch(new SnackbarWarningAction(message));
  }

  /**
   * Dispatch a reload document list action
   */
  public dispatchReloadDocumentList(): void {
    this.store.dispatch(new ReloadDocumentListAction());
  }

  /**
   * Dispatch an undo delete nodes action
   */
  public dispatchUndoDeleteNodes(nodes: any[]): void {
    this.store.dispatch(new UndoDeleteNodesAction(nodes));
  }

  /**
   * Dispatch a set selected nodes action
   */
  public dispatchSetSelectedNodes(nodes: any[]): void {
    this.store.dispatch(new SetSelectedNodesAction(nodes));
  }

  /**
   * Dispatch SetSitesAction
   */
  public dispatchSetSites(sites: any[]): void {
    this.store.dispatch(new SetSitesAction(sites));
  }

  /**
   * Dispatch SetCurrentSiteAction
   */
  public dispatchSetCurrentSite(site: any): void {
    this.store.dispatch(new SetCurrentSiteAction(site));
  }

  // ============ CustomContentManagementService Methods ============

  /**
   * Share a node and update selected nodes
   */
  public shareNode(node: NodeEntry): void {
    this.getCustomContentService().shareNode(node);
  }

  /**
   * Trigger node checkout
   */
  public checkoutNode(): void {
    this.getCustomContentService().checkoutNode();
  }

  /**
   * Print a file
   */
  public printFile(node: any): void {
    this.getCustomContentService().printFile(node);
  }

  /**
   * Navigate to preview mode
   */
  public navigatePreviewMode(node: any, nodeId: string): void {
    this.getCustomContentService().navigatePreviewMode(node, nodeId);
  }

  /**
   * Navigate to edit component
   */
  public navigateToEditComponent(
    currentLocation: string,
    folderId: string,
    nodeId: string,
    workingCopyId?: string
  ): void {
    this.getCustomContentService().navigateToEditComponent(
      currentLocation,
      folderId,
      nodeId,
      workingCopyId
    );
  }

  /**
   * Checkout node for edition and dispatch success action
   */
  public checkOutNodeForEdition(nodeId: string): Promise<string> {
    return this.getCustomContentService().checkOutNodeForEdition(nodeId);
  }

  /**
   * Assign workflow step
   */
  public assignWorkflowStep(node: NodeChildAssociationEntry): void {
    this.getCustomContentService().assignWorkflowStep(node);
  }

  /**
   * Cancel workflow
   */
  public cancelWorkflow(node: NodeChildAssociationEntry): void {
    this.getCustomContentService().cancelWorkflow(node);
  }

  // ============ WkContentManagementService Methods ============

  /**
   * Custom delete nodes
   */
  public customDeleteNodes(nodes: any[]): void {
    this.getWkContentManagementService().customDeleteNodes(nodes);
  }

  /**
   * Undo delete nodes
   */
  public undoDeleteNodes(nodes: any[]): void {
    this.getWkContentManagementService().undoDeleteNodes(nodes);
  }

  // ============ NodeInfoService Methods ============

  /**
   * Start a process
   */
  public startProcess(nodes: any[]): void {
    this.getNodeInfoService().startProcess(nodes);
  }

  /**
   * Check in nodes
   */
  public checkIn(nodeIds: string[], data: any, location: string): Promise<any> {
    return this.getNodeInfoService().checkIn(nodeIds, data, location);
  }
}
